/**
 * useSEO Hook
 * Custom hook for managing SEO metadata per page
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '../components/seo/SEO';
import { seoConfig } from '../config/seo.config';

/**
 * Get SEO config based on current route
 * @param {string} route - Current route path
 * @param {Object} dynamicData - Dynamic data for routes like job detail
 * @returns {Object} SEO configuration object
 */
const getSEOConfig = (route, dynamicData = {}) => {
  const routeMap = {
    '/': seoConfig.home,
    '/about': seoConfig.about,
    '/careers': seoConfig.careers,
    '/contact': seoConfig.contact,
    '/ourtalents': seoConfig.ourTalents,
    '/blog': seoConfig.blog,
    '/clients': seoConfig.clients,
  };

  // Handle job detail route
  if (route.startsWith('/careers/job/') && dynamicData.job) {
    return seoConfig.jobDetail(dynamicData.job);
  }

  // Handle team member route
  if (route.startsWith('/team/')) {
    return seoConfig.team;
  }

  return routeMap[route] || seoConfig.notFound;
};

/**
 * useSEO Hook
 * @param {Object} customSEO - Custom SEO overrides
 * @param {Object} dynamicData - Dynamic data for routes
 * @returns {JSX.Element} SEO component
 */
export const useSEO = (customSEO = {}, dynamicData = {}) => {
  const location = useLocation();
  const routeConfig = getSEOConfig(location.pathname, dynamicData);
  
  const seoProps = {
    ...routeConfig,
    ...customSEO, // Allow custom overrides
  };

  return <SEO {...seoProps} />;
};

export default useSEO;
